import React, { useState } from "react";
import { Helmet } from "react-helmet";
import "./Contact.css";
import Footer from "./Footer";
import mailbox from "./assets/mailbox2.png";
import { Link } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { supabase } from "./supabaseClient";
import celebrate from "./assets/celebrate.png";

const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    businessEmail: "",
    companyName: "",
    websiteUrl: "",
    businessType: "",
    workedWithSeo: "",
    primaryGoal: "",
  });
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (error) {
      setError("");
    }
  };

  const handleSubmit = async () => {
    if (
      !formData.fullName.trim() ||
      !formData.businessEmail.trim() ||
      !formData.companyName.trim() ||
      !formData.websiteUrl.trim() ||
      !formData.workedWithSeo
    ) {
      setError("Please fill out all required fields before submitting.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.businessEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    if (formData.primaryGoal.trim()) {
      const wordCount = formData.primaryGoal.trim().split(/\s+/).length;
      if (wordCount > 150) {
        setError("Primary goal must be 150 words or less.");
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const { data, error: supabaseError } = await supabase
        .from("trial_requests")
        .insert([
          {
            full_name: formData.fullName,
            business_email: formData.businessEmail,
            company_name: formData.companyName,
            website_url: formData.websiteUrl,
            business_type: formData.businessType,
            worked_with_seo_before: formData.workedWithSeo,
            primary_goal: formData.primaryGoal || null,
          },
        ]);

      if (supabaseError) throw supabaseError;

      setFormData({
        fullName: "",
        businessEmail: "",
        companyName: "",
        websiteUrl: "",
        businessType: "",
        workedWithSeo: "",
        primaryGoal: "",
      });

      setShowModal(true);
    } catch (err) {
      console.error("Error submitting form:", err);
      setError("Failed to submit form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const handleModalOutsideClick = (e) => {
    if (e.target.className === "modal-overlay") {
      closeModal();
    }
  };

  return (
    <div className="page-wrapper">
      <Helmet>
        <meta
          name="description"
          content="Get in touch with Attoli. Send us a message and we'll get back to you as soon as possible."
        />
        <title>Contact | Attoli</title>
        <link rel="canonical" href="https://attoli.com/contact" />
      </Helmet>

      <div className="contact-page-container">
        <div className="contact-page-content">
          <div className="contact-form-section">
            <h1 className="contact-page-title">Contact Form</h1>

            <p className="contact-page-description">
              By completing this short form we can assess if our relationship is
              a good match. Once approved an email will be sent in the next 24
              hours with further instructions regarding next steps and payment.
            </p>
            <p className="contact-page-reassurance">
              No long-term commitment. No phone calls unless you ask for one.
              Any questions can be sent to{" "}
              <span className="contact-email">attoli@outlook.com</span> and
              we’ll be happy to help.
            </p>

            {error && <div className="error-message">{error}</div>}

            <div className="contact-form">
              <div className="input-group">
                <input
                  type="text"
                  name="fullName"
                  placeholder="Full Name *"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className="contact-input"
                />
              </div>

              <div className="input-group">
                <input
                  type="email"
                  name="businessEmail"
                  placeholder="Business Email *"
                  value={formData.businessEmail}
                  onChange={handleInputChange}
                  className="contact-input"
                />
              </div>

              <div className="input-group">
                <input
                  type="text"
                  name="companyName"
                  placeholder="Company Name *"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  className="contact-input"
                />
              </div>

              <div className="input-group">
                <input
                  type="url"
                  name="websiteUrl"
                  placeholder="Website URL *"
                  value={formData.websiteUrl}
                  onChange={handleInputChange}
                  className="contact-input"
                />
              </div>

              <div className="input-group">
                <select
                  name="workedWithSeo"
                  value={formData.workedWithSeo}
                  onChange={handleInputChange}
                  className="contact-input contact-select"
                >
                  <option value="">
                    Have you worked with Web Management before? *
                  </option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>

              <button
                type="button"
                onClick={handleSubmit}
                className="contact-submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Reach Out"}
              </button>
            </div>
          </div>

          <div className="contact-image-section" aria-hidden="true">
            <div className="contact-image-container">
              <img src={mailbox} alt="" className="contact-image" />
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={handleModalOutsideClick}>
          <div className="modal-content">
            <button className="modal-close" onClick={closeModal}>
              ×
            </button>
            <div className="modal-image-container">
              <img src={celebrate} alt="Mailbox" className="modal-image" />
            </div>
            <h2 className="modal-title">Amazing!</h2>
            <p className="modal-text">
              Thank you for reaching out to us. We’ll review your details and
              email back you within 24 hours with next steps and payment
              instructions
            </p>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Contact;
